import * as bootstrap from 'bootstrap';
import { get, post, notifier } from '../util';
import * as Highcharts from 'highcharts';
import { updateChartTotals, setActiveStepper } from './shared';

// Module-level state
let currentDate: string = '';
let smConverting = false;  // 4-way sync guard (stairmaster cards)
let newCardCounter = 0;
let hiddenListenerAttached = false;
let modalListenersBound = false;

// Stairmaster conversion constants
const STEPS_PER_FLOOR = 10;
const KM_PER_STEP = 0.0003048;
const KM_MILES = 1.60934;

interface StairmasterData {
    stairmaster_id: number;
    floors: number | null;
    steps: number | null;
    km: number | null;
    miles: number | null;
    time: string | null;
}

/**
 * Builds the activity-cards container + per-card skeleton/data + Add Stairmaster/Save All
 * controls into the already-cleared body (Phase 262, HOME-01/02). `sessions === null` renders
 * the D-01 show-first blank skeleton card; an empty array renders the same blank-card empty
 * state (a step that resolved to zero sessions); a populated array renders one real card per
 * session. Returns the new container element.
 */
function buildSmBody(sessions: StairmasterData[] | null): HTMLElement {
    const body = document.getElementById('chart-edit-modal-body')!;

    const container = document.createElement('div');
    container.className = 'activity-cards-container';
    body.appendChild(container);

    if (sessions === null || sessions.length === 0) {
        // Skeleton: one real, enabled, blank stairmaster card so D-03's autofocus target (the
        // first field of the first card) is real and focusable from the very first paint.
        renderSmCard(container, null);
    } else {
        sessions.forEach(session => renderSmCard(container, session));
    }

    // Add button row -- stairmaster only
    const addRow = document.createElement('div');
    addRow.className = 'd-flex gap-2 mt-2';

    const addSmBtn = document.createElement('button');
    addSmBtn.type = 'button';
    addSmBtn.className = 'btn btn-outline-warning flex-fill';
    addSmBtn.textContent = '+ Stairmaster';
    addSmBtn.addEventListener('click', () => {
        const card = renderSmCard(container, null);
        container.appendChild(card);
        card.scrollIntoView({ behavior: 'smooth' });
    });

    addRow.appendChild(addSmBtn);
    body.appendChild(addRow);

    // Save All button. `data-modal-type="stairmaster"` disambiguates from runs-modal.ts's
    // Save All button, which shares the same `.js-activity-save-all` class name -- the
    // keydown listener above needs a reliable way to tell them apart.
    const saveAllBtn = document.createElement('button');
    saveAllBtn.type = 'button';
    saveAllBtn.className = 'btn btn-primary w-100 mt-2 js-activity-save-all';
    saveAllBtn.dataset.modalType = 'stairmaster';
    saveAllBtn.innerHTML = '<span class="js-activity-save-label">Save All</span><span class="spinner-border spinner-border-sm d-none js-activity-spinner" role="status"></span>';
    saveAllBtn.addEventListener('click', () => handleSaveAll(saveAllBtn));
    body.appendChild(saveAllBtn);

    return container;
}

/**
 * Fetch-and-populate half of openStairmasterModal() (Phase 262, HOME-01/02) -- extracted so the
 * date-nav step path can reload the body for a new date without closing/reopening the modal.
 *
 * Open path (blankUntilLoaded=false): behaves exactly as before -- the body is cleared and the
 * D-01 show-first skeleton card + Add Stairmaster/Save All controls are built synchronously,
 * then the first card is populated in place once getStairmasterFromDate resolves (never
 * rebuilt, so a shown.bs.modal focus already landed on it survives).
 *
 * Step path (blankUntilLoaded=true): the body is cleared and left EMPTY (P-04 -- no skeleton
 * card, no spinner) until the fetch resolves, then the whole container/cards/controls are built
 * fresh from the response via buildSmBody().
 */
function renderBodyForDate(date: string, blankUntilLoaded: boolean, done?: (ok: boolean) => void): void {
    const body = document.getElementById('chart-edit-modal-body');
    if (!body) { done?.(false); return; }
    body.innerHTML = '';

    let openContainer: HTMLElement | null = null;
    if (!blankUntilLoaded) {
        openContainer = buildSmBody(null);
    }

    get('?do=getStairmasterFromDate&date=' + date, {}, (data: any) => {
        if (blankUntilLoaded && data.status !== 1) { done?.(false); return; }

        // CR-01 stale-response guard (mirrors mood-modal.ts:207). Two getStairmasterFromDate
        // requests can be in flight for different dates -- openModal()'s own fetch is not covered
        // by date-nav's _busy lock. Dropping the stale one prevents both the wrong-date save
        // (CR-01) and the duplicated card list this builder would otherwise appendChild again (WR-01).
        if (date !== currentDate) { done?.(true); return; }

        const smData: StairmasterData[] = data.status === 1 ? (data.sessions || []) : [];

        if (!blankUntilLoaded) {
            if (smData.length === 0) {
                // Skeleton blank card already matches the empty state -- nothing to populate.
                done?.(true);
                return;
            }
            // Populate the already-rendered skeleton card in place with the first session
            // (never destroys a shown.bs.modal focus already landed on it), then append the
            // rest.
            const firstCard = openContainer!.querySelector('.sm-card') as HTMLElement | null;
            if (firstCard) {
                populateSmCard(firstCard, smData[0]);
            }
            for (let i = 1; i < smData.length; i++) {
                renderSmCard(openContainer!, smData[i]);
            }
            done?.(true);
            return;
        }

        buildSmBody(smData);
        done?.(true);
    });
}

/**
 * Date-nav step callback (Phase 262, HOME-01/02) -- the ModalDateStepper handed to shared.ts's
 * active-stepper registry from inside openStairmasterModal().
 */
function step(newDate: string, done: (ok: boolean) => void): void {
    // T-262-30: currentDate/newCardCounter are reassigned BEFORE the fetch is issued.
    currentDate = newDate;
    newCardCounter = 0;
    renderBodyForDate(newDate, true, done);
}

export function openStairmasterModal(date: string): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    currentDate = date;
    newCardCounter = 0;

    // Phase 262 HOME-01/02: register this modal's step implementation before the modal is
    // shown, next to the existing currentDate assignment.
    setActiveStepper(date, step);

    // No dedicated initStairmasterModal() export exists (RESEARCH Pitfall 6) and this plan's
    // files_modified scope excludes home.ts (no new call site can be added there), so — per
    // the runs/gym convention this plan settles on — the guard flags stay module-level and
    // idempotent, just bound inline on first open rather than from a shared init() call.
    if (!hiddenListenerAttached) {
        modalEl.addEventListener('hidden.bs.modal', () => {
            const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
            if (footer) footer.classList.remove('d-none');
            currentDate = '';
        });
        hiddenListenerAttached = true;
    }

    if (!modalListenersBound) {
        modalListenersBound = true;

        // D-03: autofocus the first field of the first card once the modal is actually
        // shown. Self-selects via `.sm-card` — unique to this modal (runs uses `.run-card`)
        // — so this is a no-op when a different modal type is rendered.
        modalEl.addEventListener('shown.bs.modal', () => {
            const firstField = modalEl.querySelector(
                '#chart-edit-modal-body .sm-card .js-sm-floors'
            ) as HTMLInputElement | null;
            firstField?.focus();
        });

        // D-04/D-05: container-level Enter-to-save. Falls through for <button> targets
        // ("+ Stairmaster" / per-card delete) so their native action is preserved.
        // Self-selects via `data-modal-type="stairmaster"` — `.js-activity-save-all` is
        // SHARED between runs and stairmaster (same class name in both files), so the class
        // alone would ambiguously match whichever of the two is currently open.
        modalEl.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key !== 'Enter') return;
            const target = e.target as HTMLElement;
            if (target.tagName === 'BUTTON') return;
            const saveAllBtn = modalEl.querySelector(
                '.js-activity-save-all[data-modal-type="stairmaster"]'
            ) as HTMLButtonElement | null;
            if (!saveAllBtn) return;
            e.preventDefault();
            handleSaveAll(saveAllBtn);
        });
    }

    // D-01 show-first: title/footer are computable synchronously (no field in them depends on
    // the AJAX response), so render + show BEFORE the getStairmasterFromDate call. P-01:
    // bare, date-free title -- the shared date-nav header owns the date.
    const title = document.getElementById('chart-edit-modal-title');
    if (title) title.textContent = 'Stairmaster';

    const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
    if (footer) footer.classList.add('d-none');

    renderBodyForDate(date, false);

    const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl, { backdrop: true, keyboard: true });
    modalInstance.show();
}

// ── Stairmaster cards ──────────────────────────────────────

function renderSmCard(container: HTMLElement, session: StairmasterData | null): HTMLElement {
    const cardId = session ? 'sm-card-' + session.stairmaster_id : 'sm-card-new-' + (++newCardCounter);

    const card = document.createElement('div');
    card.className = 'card mb-3 sm-card';
    card.id = cardId;
    card.dataset.smId = session ? String(session.stairmaster_id) : '';
    card.dataset.type = 'stairmaster';

    const header = document.createElement('div');
    header.className = 'card-header d-flex justify-content-between align-items-center py-1 px-2';
    header.innerHTML = `<small class="fw-semibold" style="color:#FF8C00">Stairmaster</small><button type="button" class="btn btn-sm btn-link text-danger p-0 js-card-delete" title="Delete">\u{1F5D1}</button>`;
    card.appendChild(header);

    const cardBody = document.createElement('div');
    cardBody.className = 'card-body py-2 px-2';

    const floorsVal = session?.floors != null ? String(session.floors) : '';
    const stepsVal  = session?.steps  != null ? String(session.steps)  : '';
    const kmVal     = session?.km     != null ? String(session.km)     : '';
    const milesVal  = session?.miles  != null ? String(session.miles)  : '';
    const timeVal   = session?.time ?? '';

    cardBody.innerHTML = `
        <div class="row g-2 mb-2">
            <div class="col-6">
                <label class="form-label form-label-sm mb-1">floors</label>
                <input type="number" step="1" min="0" inputmode="numeric" class="form-control form-control-sm js-sm-floors" value="${floorsVal}" />
            </div>
            <div class="col-6">
                <label class="form-label form-label-sm mb-1">steps</label>
                <input type="number" step="1" min="0" inputmode="numeric" class="form-control form-control-sm js-sm-steps" value="${stepsVal}" />
            </div>
        </div>
        <div class="row g-2 mb-2">
            <div class="col-6">
                <label class="form-label form-label-sm mb-1 js-sm-km-label">km</label>
                <input type="number" step="any" inputmode="decimal" class="form-control form-control-sm js-sm-km" value="${kmVal}" />
            </div>
            <div class="col-6">
                <label class="form-label form-label-sm mb-1">miles</label>
                <input type="number" step="any" inputmode="decimal" class="form-control form-control-sm js-sm-miles" value="${milesVal}" />
            </div>
        </div>
        <div class="row g-2">
            <div class="col-4">
                <label class="form-label form-label-sm mb-1">time</label>
                <input type="text" class="form-control form-control-sm js-sm-time" placeholder="HH:MM:SS" value="${timeVal}" />
            </div>
        </div>
    `;
    card.appendChild(cardBody);

    // Attach 4-way sync (floors/steps/km/miles)
    const floorsInput = card.querySelector('.js-sm-floors') as HTMLInputElement;
    const stepsInput  = card.querySelector('.js-sm-steps')  as HTMLInputElement;
    const kmInput     = card.querySelector('.js-sm-km')     as HTMLInputElement;
    const milesInput  = card.querySelector('.js-sm-miles')  as HTMLInputElement;
    const kmLabel     = card.querySelector('.js-sm-km-label') as HTMLElement;
    attachSmSync(floorsInput, stepsInput, kmInput, milesInput, kmLabel);

    // Set initial ~km label when session has floors/steps and km (likely auto-calculated)
    if ((session?.floors != null || session?.steps != null) && session?.km != null) {
        kmLabel.textContent = '~km';
    }

    const deleteBtn = header.querySelector('.js-card-delete') as HTMLButtonElement;
    deleteBtn.addEventListener('click', () => handleDeleteSm(card, session));

    container.appendChild(card);
    return card;
}

/**
 * Populate a skeleton stairmaster card (originally rendered with session=null) in place
 * once the getStairmasterFromDate response resolves — sets the real stairmaster_id/floors/
 * steps/km/miles/time values on the SAME DOM node (never rebuilds it) so a shown.bs.modal
 * focus already landed on one of its fields is never destroyed by this late response (D-01).
 */
function populateSmCard(card: HTMLElement, session: StairmasterData): void {
    card.id = 'sm-card-' + session.stairmaster_id;
    card.dataset.smId = String(session.stairmaster_id);

    const floorsInput = card.querySelector('.js-sm-floors') as HTMLInputElement | null;
    if (floorsInput) floorsInput.value = session.floors != null ? String(session.floors) : '';
    const stepsInput = card.querySelector('.js-sm-steps') as HTMLInputElement | null;
    if (stepsInput) stepsInput.value = session.steps != null ? String(session.steps) : '';
    const kmInput = card.querySelector('.js-sm-km') as HTMLInputElement | null;
    if (kmInput) kmInput.value = session.km != null ? String(session.km) : '';
    const milesInput = card.querySelector('.js-sm-miles') as HTMLInputElement | null;
    if (milesInput) milesInput.value = session.miles != null ? String(session.miles) : '';
    const timeInput = card.querySelector('.js-sm-time') as HTMLInputElement | null;
    if (timeInput) timeInput.value = session.time ?? '';

    const kmLabel = card.querySelector('.js-sm-km-label') as HTMLElement | null;
    if (kmLabel && (session.floors != null || session.steps != null) && session.km != null) {
        kmLabel.textContent = '~km';
    }

    // Re-bind delete to the now-known session — the skeleton's delete handler closed over
    // session=null (an unsaved-card removal, not a real DELETE call).
    const deleteBtn = card.querySelector('.js-card-delete') as HTMLButtonElement | null;
    if (deleteBtn) {
        const freshDeleteBtn = deleteBtn.cloneNode(true) as HTMLButtonElement;
        deleteBtn.replaceWith(freshDeleteBtn);
        freshDeleteBtn.addEventListener('click', () => handleDeleteSm(card, session));
    }
}

function attachSmSync(
    floorsInput: HTMLInputElement,
    stepsInput: HTMLInputElement,
    kmInput: HTMLInputElement,
    milesInput: HTMLInputElement,
    kmLabel: HTMLElement
): void {
    floorsInput.addEventListener('input', () => {
        if (smConverting) return;
        smConverting = true;
        const f = parseFloat(floorsInput.value);
        if (!isNaN(f) && floorsInput.value !== '') {
            stepsInput.value = String(Math.round(f * STEPS_PER_FLOOR));
            const km = (f * STEPS_PER_FLOOR * KM_PER_STEP).toFixed(3);
            kmInput.value = km;
            milesInput.value = (parseFloat(km) / KM_MILES).toFixed(3);
            kmLabel.textContent = '~km';
        } else {
            stepsInput.value = '';
            kmInput.value = '';
            milesInput.value = '';
            kmLabel.textContent = '~km';
        }
        smConverting = false;
    });

    stepsInput.addEventListener('input', () => {
        if (smConverting) return;
        smConverting = true;
        const s = parseFloat(stepsInput.value);
        if (!isNaN(s) && stepsInput.value !== '') {
            floorsInput.value = String(Math.round(s / STEPS_PER_FLOOR));
            const km = (s * KM_PER_STEP).toFixed(3);
            kmInput.value = km;
            milesInput.value = (parseFloat(km) / KM_MILES).toFixed(3);
            kmLabel.textContent = '~km';
        } else {
            floorsInput.value = '';
            kmInput.value = '';
            milesInput.value = '';
            kmLabel.textContent = '~km';
        }
        smConverting = false;
    });

    kmInput.addEventListener('input', () => {
        if (smConverting) return;
        smConverting = true;
        const km = parseFloat(kmInput.value);
        milesInput.value = !isNaN(km) && kmInput.value !== '' ? (km / KM_MILES).toFixed(3) : '';
        floorsInput.value = '';
        stepsInput.value = '';
        kmLabel.textContent = 'km';
        smConverting = false;
    });

    milesInput.addEventListener('input', () => {
        if (smConverting) return;
        smConverting = true;
        const miles = parseFloat(milesInput.value);
        kmInput.value = !isNaN(miles) && milesInput.value !== '' ? (miles * KM_MILES).toFixed(3) : '';
        floorsInput.value = '';
        stepsInput.value = '';
        kmLabel.textContent = 'km';
        smConverting = false;
    });
}

function handleDeleteSm(card: HTMLElement, session: StairmasterData | null): void {
    if (!window.confirm('Delete this session?')) return;

    if (!session || !session.stairmaster_id) {
        card.remove();
        return;
    }

    post('?do=deleteStairmaster&stairmasterId=' + session.stairmaster_id, {}, (data: any) => {
        if (data.status === 1) {
            card.remove();
            if (data.new_steps_sum !== undefined) {
                updateStairmasterChartPoint(currentDate, data.new_steps_sum);
            }
            // D-09/D-10/WARNING-1: a stairmaster delete refreshes BOTH range-scoped
            // titlebars — `data.totals` is the KEYED {km, stairmaster} shape (always
            // populated on a status===1 response), never pass the whole object to a
            // single updateChartTotals call.
            updateChartTotals('stairmaster', data.totals.stairmaster);
            updateChartTotals('km', data.totals.km);
            notifier.success('Session deleted');
        } else {
            alert(data.message || 'Failed to delete session.');
        }
    });
}

// ── Save All ───────────────────────────────────────────────

function handleSaveAll(saveAllBtn: HTMLButtonElement): void {
    if (saveAllBtn.disabled) return;   // D-04 re-entrancy guard — Enter-to-save calls this
                                        // directly, bypassing native disabled-button click suppression
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    const saveLabel = saveAllBtn.querySelector('.js-activity-save-label') as HTMLElement;
    const spinner = saveAllBtn.querySelector('.js-activity-spinner') as HTMLElement;

    saveAllBtn.disabled = true;
    saveLabel.classList.add('d-none');
    spinner.classList.remove('d-none');

    const prevError = modalEl.querySelector('.activity-inline-error');
    if (prevError) prevError.remove();

    const smCards = Array.from(document.querySelectorAll('#chart-edit-modal-body .sm-card')) as HTMLElement[];

    if (smCards.length === 0) {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');
        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
        modalInstance.hide();
        return;
    }

    const smPromises: Promise<any>[] = smCards.map(card => {
        const smIdAttr = card.dataset.smId || '';
        const stairmasterId = smIdAttr !== '' ? parseInt(smIdAttr) : null;
        const payload: Record<string, string | number> = {
            floors: (card.querySelector('.js-sm-floors') as HTMLInputElement).value,
            steps:  (card.querySelector('.js-sm-steps')  as HTMLInputElement).value,
            km:     (card.querySelector('.js-sm-km')     as HTMLInputElement).value,
            miles:  (card.querySelector('.js-sm-miles')  as HTMLInputElement).value,
            time:   (card.querySelector('.js-sm-time')   as HTMLInputElement).value,
        };
        if (stairmasterId !== null && stairmasterId > 0) payload.stairmaster_id = stairmasterId;
        return new Promise<any>((resolve, reject) => {
            post('?do=upsertStairmaster&date=' + currentDate, payload, (data: any) => {
                if (data.status === 1) {
                    // WR-02 (220-REVIEW) id write-back: stamp the returned id onto THIS
                    // card's dataset as soon as its own write lands, so a retry after a
                    // partial Save-All failure (e.g. via the Enter-triggered retry path)
                    // UPDATEs the now-known row instead of re-INSERTing a duplicate.
                    if (data.stairmaster_id !== undefined && data.stairmaster_id !== null) {
                        card.dataset.smId = String(data.stairmaster_id);
                    }
                    resolve(data);
                } else {
                    reject(data.message || 'Save failed');
                }
            });
        });
    });

    Promise.all(smPromises).then(results => {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');

        // Update stairmaster chart point from last sm result (per-day point — unchanged; a
        // per-DAY aggregate is order-independent across cards written for the same date).
        const lastSm = results[results.length - 1];
        if (lastSm?.new_steps_sum !== undefined) {
            updateStairmasterChartPoint(currentDate, lastSm.new_steps_sum);
        }
        // WR-02 (220-REVIEW) totals race fix: the last array-indexed result's `.totals` is NOT
        // reliably the most-recent range-scoped snapshot — Promise.all's concurrent POSTs
        // can resolve out of server-completion order, so the "last" array entry may have
        // been computed before a sibling card's write actually landed. Issue TWO dedicated
        // re-fetches (stairmaster affects both its own AND the km titlebar — 220-02's
        // multi-titlebar assembly, mirrored from the pre-existing keyed {km, stairmaster}
        // shape) via the existing fetchGraphData signal now that every write has settled.
        post('?do=fetchGraphData&key=stairmaster', {}, (graphResp: any) => {
            if (graphResp.success && graphResp.graph) {
                updateChartTotals('stairmaster', graphResp.graph.totals);
            } else {
                updateChartTotals('stairmaster', lastSm?.totals?.stairmaster);
            }
        });
        post('?do=fetchGraphData&key=km', {}, (graphResp: any) => {
            if (graphResp.success && graphResp.graph) {
                updateChartTotals('km', graphResp.graph.totals);
            } else {
                updateChartTotals('km', lastSm?.totals?.km);
            }
        });

        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
        modalInstance.hide();
        notifier.success('Saved');
    }).catch(errMsg => {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');

        const body = document.getElementById('chart-edit-modal-body');
        if (body) {
            const errorEl = document.createElement('div');
            errorEl.className = 'text-danger small mt-2 activity-inline-error';
            errorEl.textContent = typeof errMsg === 'string' ? errMsg : 'An error occurred. Please try again.';
            body.appendChild(errorEl);
        }
    });
}

// ── Chart update ───────────────────────────────────────────

function updateStairmasterChartPoint(date: string, newStepsSum: number | null): void {
    //@ts-ignore
    const chart = (Highcharts.charts as (Highcharts.Chart | undefined)[]).find((c: any) => c?.renderTo?.id === 'chart_stairmaster');
    if (!chart) return;

    const ts = Date.parse(date + 'T00:00:00');
    const series = chart.series[0];
    if (!series) return;

    const point = series.points.find((p: Highcharts.Point) => p.x === ts);
    if (point) {
        //@ts-ignore
        point.update(newStepsSum);
    }
}
